import React, { useState } from 'react';
import { toast } from 'sonner';
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer } from 'recharts';
import { aiInterviewsAPI, errMsg, fmtClock } from '../../../lib/aiInterviewApi';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Pill, Section } from '../recruiter/common';

const barColor = (s) => (s >= 4 ? '#10B981' : s >= 3 ? '#0076A3' : s >= 2 ? '#F59E0B' : '#EF4444');
const CONF_TONE = { high: 'green', medium: 'sky', low: 'amber' };

const OverrideDialog = ({ target, onClose, id, onDone }) => {
  const [score, setScore] = useState(3);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await aiInterviewsAPI.override(id, { competency: target.competency, score, note });
      toast.success('Score overridden');
      onClose();
      onDone();
    } catch (e) {
      toast.error(errMsg(e, 'Could not save the override'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="iv" data-testid="override-dialog">
        <DialogHeader>
          <DialogTitle>Override: {target?.competency}</DialogTitle>
          <DialogDescription>AI score {target?.score}/5. Your score replaces it in the weighted total; both are kept in the audit trail.</DialogDescription>
        </DialogHeader>
        <div className="iv-row">
          {[1, 2, 3, 4, 5].map((n) => <button type="button" key={n} className={`iv-chip ${score === n ? 'is-on' : ''}`} style={{ minWidth: 40 }} onClick={() => setScore(n)} data-testid={`override-score-${n}`}>{n}</button>)}
        </div>
        <textarea className="iv-textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why are you overriding? (required)" data-testid="override-note-input" />
        <div className="iv-row-end">
          <button type="button" className="iv-btn iv-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="iv-btn iv-btn-primary" disabled={busy || !note.trim()} onClick={save} data-testid="override-save-btn">Save override</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Competency = ({ c, i, bp, onSeek, onOverride, readOnly }) => {
  const [open, setOpen] = useState(i < 2);
  const eff = c.override_score || c.score;
  return (
    <div style={{ padding: '0.8rem 0', borderTop: i ? '1px solid #EEF2F5' : 0 }} data-testid={`competency-score-${i}`}>
      <div className="iv-row" style={{ justifyContent: 'space-between' }}>
        <span className="iv-row" style={{ gap: '0.4rem' }}>
          <b style={{ color: '#002A38' }}>{c.competency}</b>
          {bp?.must_have && <Pill tone="navy">Must-have</Pill>}
          {bp && <span className="iv-small">{Math.round(bp.weight * 100)}%</span>}
        </span>
        <span className="iv-row" style={{ gap: '0.4rem' }}>
          <Pill tone={CONF_TONE[c.confidence]}>{c.confidence} confidence</Pill>
          <b style={{ fontSize: '1.05rem' }} data-testid={`competency-score-value-${i}`}>{eff}/5</b>
        </span>
      </div>
      <div className="iv-bar" style={{ marginTop: '0.45rem' }}><i style={{ width: `${(eff / 5) * 100}%`, background: barColor(eff) }} /></div>
      {c.override_score && !readOnly && <div className="iv-small" style={{ marginTop: '0.35rem', color: '#B45309' }} data-testid={`competency-override-note-${i}`}>Overridden from AI {c.score}/5: “{c.override_note}”</div>}
      <p className="iv-small" style={{ marginTop: '0.4rem' }}>{c.rationale}</p>
      {open && c.evidence.map((ev) => (
        <div key={`${ev.turn}-${ev.quote.slice(0, 12)}`} className="iv-quote">
          <button type="button" onClick={() => onSeek(ev)} data-testid={`evidence-seek-${i}-${ev.turn}`}>{fmtClock(ev.start_ms)}</button>“{ev.quote}”
        </div>
      ))}
      <div className="iv-row" style={{ marginTop: '0.35rem' }}>
        {c.evidence.length > 0 && <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => setOpen((o) => !o)} data-testid={`evidence-toggle-${i}`}>{open ? 'Hide' : 'Show'} evidence ({c.evidence.length})</button>}
        {!readOnly && <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => onOverride(c)} data-testid={`override-btn-${i}`}>Override score</button>}
      </div>
    </div>
  );
};

export default function CompetencyScores({ d, onSeek, reload, readOnly = false }) {
  const [target, setTarget] = useState(null);
  const scores = d.report.competency_scores;
  const bpBy = Object.fromEntries((d.blueprint?.competencies || []).map((c) => [c.name, c]));
  const narrow = typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches;
  const max = narrow ? 12 : 20;
  const radar = scores.map((c) => ({ k: c.competency.length > max ? `${c.competency.slice(0, max - 1)}…` : c.competency, v: c.override_score || c.score }));
  return (
    <Section title="Competency scores" testId="competency-scores" right={<span className="iv-small">1 = no evidence · 5 = verified depth</span>}>
      {scores.length >= 3 && (
        <div style={{ height: 250 }} data-testid="competency-radar">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radar} outerRadius={narrow ? '52%' : '70%'}>
              <PolarGrid stroke="#E2E8F0" />
              <PolarAngleAxis dataKey="k" tick={{ fontSize: narrow ? 10 : 11, fill: '#475569' }} />
              <PolarRadiusAxis domain={[0, 5]} tickCount={6} tick={false} axisLine={false} />
              <Radar dataKey="v" stroke="#0076A3" fill="#00A0E0" fillOpacity={0.25} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      )}
      {scores.map((c, i) => <Competency key={c.competency} c={c} i={i} bp={bpBy[c.competency]} onSeek={onSeek} onOverride={setTarget} readOnly={readOnly} />)}
      {!readOnly && <OverrideDialog target={target} onClose={() => setTarget(null)} id={d.id} onDone={reload} />}
    </Section>
  );
}
